import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography
} from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  PartInCart,
  RepairLinkPart,
  ServiceItem
} from '../../../hooks/useTransformServiceItems/useTransformServiceItems'
import { PARTSTECH_REDIRECT_URL } from '../../../models'
import { AppContext } from '../../../providers/AppProvider/AppProvider'
import {
  useCheckRepairLinkPricesMutation,
  useEditCartRefreshPricesMutation,
  useGetRepairLinkAccessTokenMutation,
  useLazyGetServiceItemsQuery,
  useOrderCart1Mutation,
  useOrderCartMutation,
  useUpdateRepairLinkPricesMutation
} from '../../../redux/api'
import {
  InventoryPartInstanceEntry,
  RepairLinkPartInCartExpandedChecked,
  VehicleEntry
} from '../../../sdk'
import { formatCurrency } from '../../../utils'
import { checkIsEqualForTwoDecimal } from '../../../utils/checkIsEqualForTwoDecimal/checkIsEqualForTwoDecimal'
import { LoadingBackdrop } from '../../SharedUI/LoadingBackdrop'
import { useSubmitLinkAndClaim } from '../useLookupPartsDialog/useSubmitLinkAndClaim'
import { useUpdateOilFilterMappingWithCartInfo } from './updateOilFilterMappingWithCartInfo'
import { OrderPartAction, OrderPartsAttempt } from './useOrderCart'

export function getTotalPrice(partsArray: PartInCart[] | RepairLinkPart[]): {
  totalPrice: number
  numPartsToBuy: number
} {
  let totalPrice = 0
  let numPartsToBuy = 0
  for (let i = 0; i < partsArray.length; i++) {
    const part = partsArray[i]
    totalPrice = totalPrice + part.costPerUnit * part.quantity
    numPartsToBuy = numPartsToBuy + part.quantity
  }
  return { totalPrice, numPartsToBuy }
}

export const OrderCartDialog = ({
  orderPartAction,
  setOrderPartAction,
  isSubmitting,
  setIsSubmitting,
  services,
  vehicle
}: {
  orderPartAction: OrderPartAction
  setOrderPartAction: (o: OrderPartAction | null) => void
  isSubmitting: boolean
  setIsSubmitting: (b: boolean) => void
  services: ServiceItem[]
  vehicle: VehicleEntry | undefined
}) => {
  const { updateOilFilterMappingWithCartInfo } =
    useUpdateOilFilterMappingWithCartInfo()
  const curOrder: OrderPartsAttempt | undefined = useMemo(
    () => orderPartAction.orderPartsAttempts[orderPartAction.curOrderIndex],
    [orderPartAction.curOrderIndex, orderPartAction.orderPartsAttempts]
  )

  const [redirectURL, setRedirectURL] = useState('')
  const [refreshPrices, { isLoading: refreshingPartsTechPrice }] =
    useEditCartRefreshPricesMutation()
  const { accessToken, shopId } = useAccessTokenAndShopId()
  useEffect(() => {
    async function refreshPrice() {
      setIsSubmitting(true)
      let redirectURL = ''
      if (curOrder?.orderType === 'PartsTech') {
        const response = await refreshPrices({
          authorization: accessToken,
          updatePartsTechQuoteRequest: {
            service: curOrder.serviceItem.id!,
            redirectUrl: PARTSTECH_REDIRECT_URL
          }
        }).unwrap()
        redirectURL = response.redirectURL || ''
      }
      setRedirectURL(redirectURL)
      setIsSubmitting(false)
    }
    refreshPrice()
  }, [accessToken, curOrder, refreshPrices, setIsSubmitting])
  const curNumParts = useMemo(
    () =>
      getTotalPrice(
        curOrder.orderType === 'PartsTech'
          ? curOrder?.serviceItem.partsInCart || []
          : curOrder?.serviceItem.repairLinkPartsInCart || []
      ).numPartsToBuy,
    [curOrder]
  )
  const curTotalPrice = useMemo(
    () =>
      getTotalPrice(
        curOrder.orderType === 'PartsTech'
          ? curOrder?.serviceItem.partsInCart || []
          : curOrder?.serviceItem.repairLinkPartsInCart || []
      ).totalPrice,
    [
      curOrder?.serviceItem.partsInCart,
      curOrder.orderType,
      curOrder?.serviceItem.repairLinkPartsInCart
    ]
  )
  const [selectedToCreateCore, setSelectedToCreateCore] = useState<string[]>([])

  const { shopProfile } = useContext(AppContext)
  const { id } = useParams()
  const [triggerGetServices] = useLazyGetServiceItemsQuery()
  const [orderCart] = useOrderCartMutation()
  const [orderRepairLinkCart, { isLoading: orderingRepairLinkCart }] =
    useOrderCart1Mutation()
  const [checkRepairLinkPrices, { isLoading: checkingPrices }] =
    useCheckRepairLinkPricesMutation()
  const [updateRepairLinkPrices, { isLoading: updatingPrices }] =
    useUpdateRepairLinkPricesMutation()
  const [getRepairLinkAccessToken, { isLoading: fetchingAccessToken }] =
    useGetRepairLinkAccessTokenMutation()
  const { submitCreateCores } = useSubmitLinkAndClaim()
  const [serviceToInstances, setServiceToInstances] = useState<{
    [key: string]: Set<string>
  }>({})

  const partWithCoreNameMap: { [key: string]: string } = useMemo(() => {
    const temp: { [key: string]: string } = {}
    orderPartAction.orderPartsAttempts
      .flatMap((s) => s.serviceItem.partsInCart)
      .filter((p) => p.corePerUnit)
      .forEach((part) => {
        temp[part.id!] = part.partName!
      })
    return temp
  }, [orderPartAction.orderPartsAttempts])

  const partWithCoreCostMap: { [key: string]: number } = useMemo(() => {
    const temp: { [key: string]: number } = {}
    orderPartAction.orderPartsAttempts
      .flatMap((s) => s.serviceItem.partsInCart)
      .filter((p) => p.corePerUnit)
      .forEach((part) => {
        temp[part.id!] = part.corePerUnit!
      })
    return temp
  }, [orderPartAction.orderPartsAttempts])

  const submitCallback = useCallback(async () => {
    setOrderPartAction(null)
  }, [setOrderPartAction])

  const handlePurchasePartsTech = useCallback(async () => {
    if (!curOrder) {
      return
    }
    const serviceItem = curOrder.serviceItem
    setIsSubmitting(true)
    const response = await orderCart({
      authorization: accessToken,
      body: serviceItem.id!
    }).unwrap()
    setServiceToInstances({
      ...serviceToInstances,
      [serviceItem.id!]: new Set(response.instanceIds!)
    })
    // update oil filter mapping, no need to wait
    updateOilFilterMappingWithCartInfo(response, vehicle)
    if (
      orderPartAction.curOrderIndex <
      orderPartAction.orderPartsAttempts.length - 1
    ) {
      // increment the index and update url
      const newIndex = orderPartAction.curOrderIndex + 1
      const newAttempts = [...orderPartAction.orderPartsAttempts]
      setOrderPartAction({
        orderPartsAttempts: newAttempts,
        curOrderIndex: newIndex
      })
    } else if (Object.keys(partWithCoreNameMap).length > 0) {
      // refreshServices
      await triggerGetServices({
        authorization: accessToken,
        shop: shopId,
        repairOrder: [id!]
      })
      setSelectedToCreateCore(Object.keys(partWithCoreNameMap))
      setOrderPartAction({
        ...orderPartAction,
        curOrderIndex: orderPartAction.curOrderIndex + 1
      })
    } else {
      await submitCallback()
    }
    setIsSubmitting(false)
  }, [
    curOrder,
    setIsSubmitting,
    orderCart,
    accessToken,
    serviceToInstances,
    updateOilFilterMappingWithCartInfo,
    vehicle,
    orderPartAction,
    partWithCoreNameMap,
    setOrderPartAction,
    triggerGetServices,
    shopId,
    id,
    submitCallback
  ])

  const handleRefreshRepairLinkPrices = useCallback(async () => {
    if (!curOrder || !shopProfile?.info?.repairLinkCredentialsSet) {
      return
    }
    const serviceItem = curOrder.serviceItem
    const response = await getRepairLinkAccessToken({
      authorization: accessToken,
      shop: shopId
    }).unwrap()
    updateRepairLinkPrices({
      authorization: accessToken,
      service: serviceItem.id,
      accessToken: 'Bearer ' + response
    })
  }, [
    accessToken,
    curOrder,
    shopProfile?.info?.repairLinkCredentialsSet,
    updateRepairLinkPrices,
    shopId,
    getRepairLinkAccessToken
  ])

  const [checkedParts, setCheckedParts] = useState<
    RepairLinkPartInCartExpandedChecked[] | undefined
  >()

  const handleCheckRepairLinkPrices = useCallback(async () => {
    if (!curOrder || !shopProfile?.info?.repairLinkCredentialsSet) {
      return
    }
    const serviceItem = curOrder.serviceItem
    const response = await getRepairLinkAccessToken({
      authorization: accessToken,
      shop: shopId
    }).unwrap()
    const checkedPartsFetched = await checkRepairLinkPrices({
      authorization: accessToken,
      service: serviceItem.id,
      accessToken: 'Bearer ' + response
    }).unwrap()
    return checkedPartsFetched
  }, [
    accessToken,
    checkRepairLinkPrices,
    curOrder,
    getRepairLinkAccessToken,
    shopId,
    shopProfile?.info?.repairLinkCredentialsSet
  ])

  useEffect(() => {
    if (checkedParts !== undefined) {
      return
    }
    handleCheckRepairLinkPrices().then((response) => {
      if (response !== undefined) {
        setCheckedParts(response)
      }
    })
  }, [handleCheckRepairLinkPrices, checkedParts])

  const handlePurchaseRepairLink = useCallback(async () => {
    if (
      !curOrder ||
      !shopProfile?.info?.repairLinkCredentialsSet ||
      checkedParts === undefined
    ) {
      return
    }
    const serviceItem = curOrder.serviceItem
    setIsSubmitting(true)
    const response = await getRepairLinkAccessToken({
      authorization: accessToken,
      shop: shopId
    }).unwrap()

    await orderRepairLinkCart({
      authorization: accessToken,
      updateOrderRepairLinkRequest: {
        service: serviceItem.id,
        accessToken: 'Bearer ' + response,
        checkedParts
      }
    })
    if (
      orderPartAction.curOrderIndex <
      orderPartAction.orderPartsAttempts.length - 1
    ) {
      // increment the index and update url
      const newIndex = orderPartAction.curOrderIndex + 1
      const newAttempts = [...orderPartAction.orderPartsAttempts]
      setOrderPartAction({
        orderPartsAttempts: newAttempts,
        curOrderIndex: newIndex
      })
    } else if (Object.keys(partWithCoreNameMap).length > 0) {
      // refreshServices
      await triggerGetServices({
        authorization: accessToken,
        shop: shopId,
        repairOrder: [id!]
      })
      setSelectedToCreateCore(Object.keys(partWithCoreNameMap))
      setOrderPartAction({
        ...orderPartAction,
        curOrderIndex: orderPartAction.curOrderIndex + 1
      })
    } else {
      await submitCallback()
    }
    setIsSubmitting(false)
  }, [
    accessToken,
    curOrder,
    id,
    orderRepairLinkCart,
    orderPartAction,
    partWithCoreNameMap,
    setIsSubmitting,
    setOrderPartAction,
    shopId,
    submitCallback,
    triggerGetServices,
    getRepairLinkAccessToken,
    shopProfile?.info?.repairLinkCredentialsSet,
    checkedParts
  ])

  const handleCreateCore = useCallback(async () => {
    if (selectedToCreateCore.length === 0) {
      setOrderPartAction(null)
      return
    }
    const instancesToAddCore: InventoryPartInstanceEntry[] = []
    const overrideToInstances: { [key: string]: InventoryPartInstanceEntry[] } =
      {}
    services.forEach((service) => {
      Object.keys(service.partOverrideToLinkIdInventory!).forEach(
        (override) => {
          overrideToInstances[override] = Object.values(
            service.partOverrideToLinkIdInventory![override]
          )
        }
      )
    })
    selectedToCreateCore.forEach((override) =>
      instancesToAddCore.push(...(overrideToInstances[override] || []))
    )
    setIsSubmitting(true)
    await submitCreateCores(instancesToAddCore)
    await submitCallback()
    setIsSubmitting(false)
  }, [
    selectedToCreateCore,
    services,
    setIsSubmitting,
    setOrderPartAction,
    submitCallback,
    submitCreateCores
  ])

  return (
    <Dialog open={true} maxWidth="xs" fullWidth={true}>
      {isSubmitting && <LoadingBackdrop />}
      {curOrder && (
        <>
          <DialogTitle>{`Confirm your purchase of Cart ${
            orderPartAction.curOrderIndex + 1
          }`}</DialogTitle>
          <DialogContent>
            <>
              {curOrder.orderType === 'RepairLink' ? (
                <>
                  {fetchingAccessToken && (
                    <>
                      <DialogContentText>
                        Authenticating With RepairLink...
                      </DialogContentText>
                    </>
                  )}
                  {updatingPrices && (
                    <>
                      <DialogContentText>
                        Updating RepairLink Prices...
                      </DialogContentText>
                    </>
                  )}
                  {checkingPrices && (
                    <>
                      <DialogContentText>
                        Checking RepairLink Prices...
                      </DialogContentText>
                    </>
                  )}
                  {orderingRepairLinkCart && (
                    <>
                      <DialogContentText>
                        Ordering RepairLink Cart...
                      </DialogContentText>
                    </>
                  )}
                </>
              ) : (
                <>
                  {refreshingPartsTechPrice && (
                    <>
                      <DialogContentText>
                        Checking PartsTech Prices...
                      </DialogContentText>
                    </>
                  )}
                </>
              )}
              {!checkIsEqualForTwoDecimal(curOrder.prevPrice, curTotalPrice) ? (
                <>
                  Warning! Prices have changed!
                  <br></br>
                  <br></br>
                </>
              ) : (
                ''
              )}
              <Typography variant="subtitle1" gutterBottom>
                Buying {curNumParts} {curNumParts === 1 ? 'Part' : 'Parts'} for
                Total Price: {formatCurrency(curTotalPrice, true)}
              </Typography>
              {curOrder.orderType === 'PartsTech' &&
                curOrder.serviceItem.partsInCart.map((part) => (
                  <Typography key={part.id} noWrap>
                    - {part.quantity} x {part.partName}
                  </Typography>
                ))}
              {curOrder.orderType === 'RepairLink' &&
                curOrder.serviceItem.repairLinkPartsInCart.map((part) => (
                  <Typography key={part.id} noWrap>
                    - {part.quantity} x {part.partName}
                  </Typography>
                ))}
              {redirectURL && (
                <Button
                  sx={{ mt: 1 }}
                  variant="outlined"
                  onClick={() => window.open(redirectURL)}>
                  View Cart
                </Button>
              )}
              {curOrder.orderType === 'RepairLink' && (
                <Button
                  sx={{ mt: 1 }}
                  variant="outlined"
                  onClick={handleRefreshRepairLinkPrices}>
                  Refresh Prices
                </Button>
              )}
            </>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOrderPartAction(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                curOrder.orderType === 'RepairLink' &&
                checkedParts === undefined
              }
              onClick={
                curOrder.orderType === 'PartsTech'
                  ? handlePurchasePartsTech
                  : handlePurchaseRepairLink
              }>
              Buy Now
            </Button>
          </DialogActions>
        </>
      )}
      {!curOrder && selectedToCreateCore && (
        <>
          <DialogTitle>Create Core Item</DialogTitle>
          <DialogContent>
            Create core items for:
            {Object.keys(partWithCoreNameMap).map((override) => (
              <FormGroup key={override}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedToCreateCore.includes(override)}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        if (event.target.checked) {
                          setSelectedToCreateCore([
                            ...selectedToCreateCore,
                            override
                          ])
                        } else {
                          setSelectedToCreateCore(
                            selectedToCreateCore.filter(
                              (key) => key !== override
                            )
                          )
                        }
                      }}
                    />
                  }
                  label={`${
                    partWithCoreNameMap[override]
                  } Core Unit Price: ${formatCurrency(
                    partWithCoreCostMap[override],
                    true
                  )}`}
                />
              </FormGroup>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOrderPartAction(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateCore}>
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
